import React from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { AlertTriangle, Plus, Pencil, Trash2, FileText, ClipboardList, ExternalLink, Link2, Copy, Check, RefreshCw } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { SlidePanel } from '../../components/ui/SlidePanel'
import { KommerSnart } from '../../components/ui/KommerSnart'
import { ComplianceDisclaimer } from '../../components/ComplianceDisclaimer'
import { apiFetch, openPdfInNewTab } from '../../lib/api-client'
import { useSynStatus, showMoveInWarning } from '../../lib/syn-status'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Contract {
  id: string
  lease_id: string
  type: string
  monthly_rent_oere: number
  deposit_oere: number
  prepaid_rent_oere: number
  start_date: string
  end_date: string | null
  signed_at: string | null
  landlord_signed_at: string | null
  status: string
  serial_number: number | null
  regulation: boolean
  nettoprisindeks: boolean
  notes: string | null
  opsagt_dato?: string | null
  opsagt_af?: string | null
  opsigelse_ground?: string | null
  fraflytning_dato?: string | null
  indsigelse_frist?: string | null
}

// ─── Opsigelse types ─────────────────────────────────────────────────────────
//
// Mirrors `services/opsigelse.py` + `routers/contracts.py::OpsigRequest`.
// The lejer 3-mdr-til-den-1. and §171 nr.5 refusal are enforced server-side;
// the frontend just shows the right grounds and surfaces the error.

type OpsigParty = 'lejer' | 'udlejer'

interface OpsigGroundOption {
  value: string
  label: string
  helper?: string
}

const LEJER_GROUNDS: OpsigGroundOption[] = [
  {
    value: 'beboelseslejlighed',
    label: 'Beboelseslejlighed (3 mdr. til den 1.)',
    helper: 'Lejers opsigelse af en almindelig bolig. Kontrakten kan forkorte varslet — aldrig forlænge det.',
  },
  {
    value: 'vaerelse_uden_koekken',
    label: 'Værelse uden eget køkken (1 md. til månedens udgang)',
  },
  {
    value: 'tidsbegraenset',
    label: 'Tidsbegrænset — kontrakten udløber (§ 162)',
    helper: 'Fraflytningsdato sættes lig kontraktens slutdato.',
  },
]

const UDLEJER_GROUNDS: OpsigGroundOption[] = [
  { value: '§170_nr1', label: '§ 170 nr. 1 — Værelse i udlejers egen bolig (1 md.)' },
  { value: '§170_nr2', label: '§ 170 nr. 2 — Udlejer afhænder ejendommen (12 mdr.)' },
  { value: '§170_nr3', label: '§ 170 nr. 3 — Udlejer skal selv bebo (12 mdr.)' },
  {
    value: '§171_nr1',
    label: '§ 171 nr. 1 — Eget brug (mindst 365 dage)',
    helper: 'Husk § 174 erstatningsbolig hvis et andet ledigt lejemål findes inden 3 mdr.',
  },
  {
    value: '§171_nr2',
    label: '§ 171 nr. 2 — Nedrivning / ombygning (3 mdr.)',
    helper: 'Kræver uploadet dokumentationsbilag (byggetilladelse, byggesagsbeskrivelse, …).',
  },
  { value: '§171_nr3', label: '§ 171 nr. 3 — Væsentlig misligholdelse (3 mdr.)' },
  { value: '§171_nr4', label: '§ 171 nr. 4 — Tilsidesættelse af god skik og orden (3 mdr.)' },
]

const GROUND_LABEL: Record<string, string> = Object.fromEntries(
  [...LEJER_GROUNDS, ...UDLEJER_GROUNDS].map((g) => [g.value, g.label]),
)

interface ContractParty {
  renter_id: string
  role: string
  signed_at: string | null
  name: string
  email: string
}

interface VarslingPreview {
  contract_id: string
  from_year: number
  to_year: number
  from_index: number
  to_index: number
  pct_increase_raw: number
  pct_increase_applied: number
  was_capped: boolean
  current_rent_oere: number
  new_rent_oere: number
  regulation_effective_date: string
  varsling_deadline: string
  varsling_ok: boolean
  warning: string | null
}

interface Lease {
  id: string
  property_id: string
  unit_no: string
  serial_number: number | null
}

interface Property {
  id: string
  address: string
  serial_label: string
}

interface BillingLine {
  id: string
  contract_id: string
  gl_account: string
  description: string
  amount_oere: number
  line_group: string
  sort_order: number
  source: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusVariant(s: string): 'green' | 'blue' | 'amber' | 'gray' | 'purple' | 'red' {
  switch (s) {
    case 'active': return 'green'
    case 'signed': return 'blue'
    case 'sent': return 'purple'
    case 'draft': return 'gray'
    case 'terminated': return 'red'
    default: return 'gray'
  }
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    draft: 'Kladde', sent: 'Sendt', signed: 'Underskrevet',
    active: 'Aktiv', terminated: 'Opsagt',
  }
  return map[s] ?? s
}

function fmtRent(oere: number): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(oere / 100)
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' })
}

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: '#9ca3af',
  textTransform: 'uppercase', letterSpacing: '0.07em',
  padding: '10px 20px 4px',
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ padding: '10px 20px', fontSize: 13, color: '#6b7280', width: 200, whiteSpace: 'nowrap' }}>
        {label}
      </td>
      <td style={{ padding: '10px 20px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
        {value ?? <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
    </tr>
  )
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'overblik' | 'lejere' | 'opkraevning' | 'fakturering' | 'syn' | 'klausuler' | 'nogleetal' | 'dokumenter' | 'billeder' | 'beskeder' | 'opgaver' | 'historik'

const TABS: { key: Tab; label: string }[] = [
  { key: 'overblik', label: 'Overblik' },
  { key: 'lejere', label: 'Lejere' },
  { key: 'opkraevning', label: 'Opkrævning' },
  { key: 'fakturering', label: 'Opkrævninger' },
  { key: 'syn', label: 'Syn' },
  { key: 'klausuler', label: 'Klausuler' },
  { key: 'nogleetal', label: 'Nøgletal' },
  { key: 'dokumenter', label: 'Dokumenter' },
  { key: 'billeder', label: 'Billeder' },
  { key: 'beskeder', label: 'Beskeder' },
  { key: 'opgaver', label: 'Opgaver' },
  { key: 'historik', label: 'Historik' },
]

// ─── Edit Stamdata Panel ──────────────────────────────────────────────────────

function EditStamdataPanel({ contract, onClose, onSaved }: {
  contract: Contract
  onClose: () => void
  onSaved: (c: Contract) => void
}) {
  const [form, setForm] = React.useState({
    start_date: contract.start_date,
    end_date: contract.end_date ?? '',
    regulation: contract.regulation,
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const updated = await apiFetch<Contract>(`/api/v1/contracts/${contract.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          regulation: form.regulation,
        }),
      })
      onSaved(updated)
      onClose()
    } catch {
      setError('Kunne ikke gemme ændringer. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  const inputWrap: React.CSSProperties = { marginBottom: 14 }
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: '#6b7280', marginBottom: 6 }

  return (
    <form onSubmit={handleSave}>
      <div style={inputWrap}>
        <label style={labelStyle}>Indflytningsdato</label>
        <input
          type="date"
          value={form.start_date}
          onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }}
        />
      </div>
      <div style={inputWrap}>
        <label style={labelStyle}>Ophørsdato (tom = tidsubegrænset)</label>
        <input
          type="date"
          value={form.end_date}
          onChange={(e) => setForm({ ...form, end_date: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit', boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.regulation}
            onChange={(e) => setForm({ ...form, regulation: e.target.checked })}
          />
          Reguleret
        </label>
      </div>

      {error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

// ─── Overblik Tab ─────────────────────────────────────────────────────────────

function OverblikTab({ contract, onUpdated }: { contract: Contract; onUpdated: (c: Contract) => void }) {
  const [editOpen, setEditOpen] = React.useState(false)
  const [notes, setNotes] = React.useState(contract.notes ?? '')
  const [savingNotes, setSavingNotes] = React.useState(false)
  const synStatus = useSynStatus(contract.id)

  const handleNotesBlur = async () => {
    if (notes === (contract.notes ?? '')) return
    setSavingNotes(true)
    try {
      const updated = await apiFetch<Contract>(`/api/v1/contracts/${contract.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: notes || null }),
      })
      onUpdated(updated)
    } catch { /* ignore */ } finally {
      setSavingNotes(false)
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid #e8eaed',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={SECTION_LABEL as React.CSSProperties}>STAMDATA</span>
            <Button size="sm" onClick={() => setEditOpen(true)}>Rediger</Button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <Row label="Type" value={contract.type.toUpperCase()} />
              <Row label="Status" value={<Chip variant={statusVariant(contract.status)}>{statusLabel(contract.status)}</Chip>} />
              <Row label="Indflytning" value={fmtDate(contract.start_date)} />
              <Row label="Ophør" value={contract.end_date ? fmtDate(contract.end_date) : 'Tidsubegrænset'} />
              <Row label="Underskrevet" value={fmtDate(contract.signed_at)} />
              <Row label="Månedsleje" value={fmtRent(contract.monthly_rent_oere)} />
              <Row label="Depositum" value={fmtRent(contract.deposit_oere)} />
              <Row label="Forudbetalt leje" value={fmtRent(contract.prepaid_rent_oere)} />
              <Row label="Reguleret" value={contract.regulation ? 'Ja' : 'Nej'} />
              {synStatus?.applies && showMoveInWarning(synStatus.move_in) && (
                <Row
                  label="Indflytningssyn"
                  value={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#b45309' }}>
                      <AlertTriangle size={13} aria-hidden />
                      Indflytningssyn ikke afholdt rettidigt (frist {fmtDate(synStatus.move_in.deadline)})
                    </span>
                  }
                />
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e8eaed',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={SECTION_LABEL as React.CSSProperties}>NOTATER</span>
          {savingNotes && <span style={{ fontSize: 11, color: '#9ca3af' }}>Gemmer…</span>}
        </div>
        <div style={{ padding: 16 }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            placeholder="Notater kan anføres her..."
            style={{
              width: '100%', minHeight: 160, border: '1px solid #e8eaed',
              borderRadius: 6, padding: 10, fontSize: 13, color: '#111827',
              fontFamily: 'inherit', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box', lineHeight: 1.5,
            }}
          />
        </div>
      </div>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger lejeforhold">
        <EditStamdataPanel contract={contract} onClose={() => setEditOpen(false)} onSaved={onUpdated} />
      </SlidePanel>
    </div>
  )
}

// ─── Opkrævning Tab ───────────────────────────────────────────────────────────

type BillingPanel = { mode: 'add' } | { mode: 'edit'; line: BillingLine } | null

function OpkraevningTab({ contractId, contractStatus }: { contractId: string; contractStatus: string }) {
  const [lines, setLines] = React.useState<BillingLine[]>([])
  const [loading, setLoading] = React.useState(true)
  const [panel, setPanel] = React.useState<BillingPanel>(null)
  const [rederiving, setRederiving] = React.useState(false)
  const [rederiveError, setRederiveError] = React.useState<string | null>(null)

  React.useEffect(() => {
    apiFetch<BillingLine[]>(`/api/v1/contracts/${contractId}/billing-lines`)
      .then(setLines)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [contractId])

  const handleDelete = async (lineId: string) => {
    try {
      await apiFetch(`/api/v1/contracts/${contractId}/billing-lines/${lineId}`, { method: 'DELETE' })
      setLines((prev) => prev.filter((l) => l.id !== lineId))
    } catch { /* ignore */ }
  }

  const handleRederive = async () => {
    if (!window.confirm('Auto-linjer genberegnes fra kontrakten. Manuelle linjer bevares.')) return
    setRederiving(true)
    setRederiveError(null)
    try {
      const updated = await apiFetch<BillingLine[]>(
        `/api/v1/contracts/${contractId}/rederive-billing`,
        { method: 'POST' },
      )
      setLines(updated)
    } catch {
      setRederiveError('Kunne ikke gendanne auto-linjer. Prøv igen.')
    } finally {
      setRederiving(false)
    }
  }

  const groups: { key: string; label: string }[] = [
    { key: 'leje', label: 'Leje' },
    { key: 'forbrug', label: 'Forbrug' },
    { key: 'ovrige', label: 'Øvrige' },
  ]

  const totalOere = lines.reduce((sum, l) => sum + l.amount_oere, 0)

  return (
    <>
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e8eaed',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Månedlige opkrævninger
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRederive}
              disabled={rederiving || contractStatus !== 'active'}
              title={contractStatus !== 'active'
                ? 'Auto-linjer kan kun gendannes på et aktivt lejeforhold'
                : 'Genberegn auto-linjer fra kontrakten'}
            >
              <RefreshCw size={12} style={{ marginRight: 4 }} />
              {rederiving ? 'Gendanner…' : 'Gendan fra kontrakt'}
            </Button>
            <Button size="sm" onClick={() => setPanel({ mode: 'add' })}>
              <Plus size={12} style={{ marginRight: 4 }} />
              Tilføj linje
            </Button>
          </div>
        </div>

        <div style={{ padding: '8px 20px', fontSize: 12, color: '#9ca3af', borderBottom: '1px solid #f3f4f6' }}>
          Auto-linjer beregnes fra kontrakten og opdateres automatisk. Manuelle linjer bevares.
        </div>
        {rederiveError && (
          <div style={{ padding: '8px 20px', fontSize: 12, color: '#dc2626', borderBottom: '1px solid #f3f4f6', background: '#fef2f2' }}>
            {rederiveError}
          </div>
        )}

        {loading && (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>
        )}

        {!loading && lines.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
            Ingen opkrævningslinjer registreret.
          </div>
        )}

        {!loading && lines.length > 0 && (
          <>
            {groups.map(({ key, label }) => {
              const groupLines = lines.filter((l) => l.line_group === key)
              if (groupLines.length === 0) return null
              return (
                <div key={key}>
                  <div style={{
                    padding: '8px 20px', fontSize: 11, fontWeight: 600,
                    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em',
                    background: '#f9fafb', borderBottom: '1px solid #e8eaed',
                  }}>
                    {label}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {groupLines.map((line, i) => (
                        <tr
                          key={line.id}
                          style={{ borderBottom: i < groupLines.length - 1 ? '1px solid #f3f4f6' : '1px solid #e8eaed' }}
                        >
                          <td style={{ padding: '10px 20px', color: '#6b7280', fontSize: 12 }}>{line.gl_account}</td>
                          <td style={{ padding: '10px 20px', color: '#111827' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {line.description}
                              {line.source === 'derived' && (
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: '2px 7px',
                                  borderRadius: 20, background: '#f3f4f6', color: '#6b7280',
                                }}>Auto</span>
                              )}
                              {line.source === 'manual' && (
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: '2px 7px',
                                  borderRadius: 20, background: '#eff6ff', color: '#4f46e5',
                                }}>Manuel</span>
                              )}
                            </span>
                          </td>
                          <td style={{ padding: '10px 20px', color: '#111827', fontWeight: 600, textAlign: 'right' }}>
                            {fmtRent(line.amount_oere)}
                          </td>
                          <td style={{ padding: '10px 20px', textAlign: 'right', width: 80, whiteSpace: 'nowrap' }}>
                            <button
                              onClick={() => setPanel({ mode: 'edit', line })}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 4, display: 'inline-flex', marginRight: 4 }}
                              title="Rediger linje"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => handleDelete(line.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, borderRadius: 4, display: 'inline-flex' }}
                              title="Slet linje"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            })}
            <div style={{
              padding: '12px 20px', borderTop: '2px solid #e8eaed',
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
              fontSize: 13, fontWeight: 700, color: '#111827',
            }}>
              <span style={{ color: '#6b7280', fontWeight: 400 }}>Total pr. måned</span>
              {fmtRent(totalOere)}
            </div>
          </>
        )}
      </div>

      <SlidePanel
        isOpen={panel !== null}
        onClose={() => setPanel(null)}
        title={panel?.mode === 'edit' ? 'Rediger opkrævningslinje' : 'Tilføj opkrævningslinje'}
      >
        {panel && (
          <BillingLineFormPanel
            contractId={contractId}
            editLine={panel.mode === 'edit' ? panel.line : null}
            onClose={() => setPanel(null)}
            onSaved={(line) => {
              setLines((prev) => {
                const idx = prev.findIndex((l) => l.id === line.id)
                if (idx === -1) return [...prev, line]
                const next = prev.slice()
                next[idx] = line
                return next
              })
              setPanel(null)
            }}
          />
        )}
      </SlidePanel>
    </>
  )
}

function BillingLineFormPanel({ contractId, editLine, onClose, onSaved }: {
  contractId: string
  editLine: BillingLine | null
  onClose: () => void
  onSaved: (line: BillingLine) => void
}) {
  const isEdit = editLine !== null
  const [form, setForm] = React.useState({
    gl_account: editLine?.gl_account ?? '',
    description: editLine?.description ?? '',
    amount_dkk: editLine ? (editLine.amount_oere / 100).toString() : '',
    line_group: editLine?.line_group ?? 'leje',
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = parseFloat(form.amount_dkk)
    if (isNaN(amount) || amount <= 0) {
      setError('Beløb skal være et positivt tal.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (isEdit && editLine) {
        const updated = await apiFetch<BillingLine>(
          `/api/v1/contracts/${contractId}/billing-lines/${editLine.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              gl_account: form.gl_account.trim() || '9999',
              description: form.description.trim(),
              amount_oere: Math.round(amount * 100),
              line_group: form.line_group,
            }),
          },
        )
        onSaved(updated)
      } else {
        const created = await apiFetch<BillingLine>(`/api/v1/contracts/${contractId}/billing-lines`, {
          method: 'POST',
          body: JSON.stringify({
            gl_account: form.gl_account.trim() || '9999',
            description: form.description.trim(),
            amount_oere: Math.round(amount * 100),
            line_group: form.line_group,
            sort_order: 0,
          }),
        })
        onSaved(created)
      }
    } catch {
      setError(isEdit ? 'Kunne ikke gemme linjen. Prøv igen.' : 'Kunne ikke tilføje linjen. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  const inputWrap: React.CSSProperties = { marginBottom: 14 }
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: '#6b7280', marginBottom: 6 }

  return (
    <form onSubmit={handleSave}>
      <div style={inputWrap}>
        <label style={labelStyle}>Kontonr. (GL)</label>
        <Input value={form.gl_account} onChange={(e) => setForm({ ...form, gl_account: e.target.value })} placeholder="1000" />
      </div>
      <div style={inputWrap}>
        <Input
          label="Beskrivelse"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Husleje"
          autoFocus
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Beløb pr. måned (DKK)"
          type="number"
          value={form.amount_dkk}
          onChange={(e) => setForm({ ...form, amount_dkk: e.target.value })}
          placeholder="7500"
        />
      </div>
      <div style={inputWrap}>
        <label style={labelStyle}>Gruppe</label>
        <select
          value={form.line_group}
          onChange={(e) => setForm({ ...form, line_group: e.target.value })}
          style={{ height: 40, padding: '0 12px', borderRadius: 7, border: '1px solid #e8eaed', fontSize: 14, width: '100%', fontFamily: 'inherit' }}
        >
          <option value="leje">Leje</option>
          <option value="forbrug">Forbrug</option>
          <option value="ovrige">Øvrige</option>
        </select>
      </div>

      {error && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>{isEdit ? 'Gem' : 'Tilføj'}</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

// ─── Invoice types ────────────────────────────────────────────────────────────

interface Invoice {
  id: string
  invoice_number: string | null
  type: string
  title: string
  amount_oere: number
  issue_date: string | null
  due_date: string | null
  status: string
}

function invoiceStatusVariant(s: string): 'green' | 'blue' | 'amber' | 'gray' | 'purple' | 'red' {
  switch (s) {
    case 'paid': return 'green'
    case 'sent': return 'blue'
    case 'overdue': return 'red'
    case 'draft': return 'gray'
    case 'cancelled': return 'gray'
    default: return 'gray'
  }
}

function invoiceStatusLabel(s: string): string {
  const map: Record<string, string> = {
    draft: 'Kladde', sent: 'Sendt', paid: 'Betalt',
    overdue: 'Forfalden', cancelled: 'Annulleret',
  }
  return map[s] ?? s
}

// ─── Fakturering Tab (invoice history) ───────────────────────────────────────

function FaktureringTab({ contractId }: { contractId: string }) {
  const [invoices, setInvoices] = React.useState<Invoice[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    apiFetch<{ items: Invoice[]; total: number }>(`/api/v1/invoices?contract_id=${contractId}`)
      .then((res) => setInvoices(res.items))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [contractId])

  const thStyle: React.CSSProperties = {
    padding: '8px 16px', textAlign: 'left', fontSize: 11,
    fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid #e8eaed',
    background: '#f9fafb', whiteSpace: 'nowrap',
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #e8eaed',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Fakturahistorik
        </span>
      </div>

      {loading && (
        <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>
      )}

      {!loading && invoices.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <FileText size={28} style={{ color: '#d1d5db', marginBottom: 8 }} />
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Ingen opkrævninger endnu</p>
        </div>
      )}

      {!loading && invoices.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={thStyle}>Fakturanr</th>
              <th style={thStyle}>Titel</th>
              <th style={thStyle}>Beløb</th>
              <th style={thStyle}>Forfald</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>PDF</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, i) => (
              <tr
                key={inv.id}
                style={{ borderBottom: i < invoices.length - 1 ? '1px solid #f3f4f6' : 'none' }}
              >
                <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: 12, color: '#4f46e5' }}>
                  {inv.invoice_number ?? '—'}
                </td>
                <td style={{ padding: '10px 16px', color: '#111827' }}>{inv.title}</td>
                <td style={{ padding: '10px 16px', color: '#111827', fontWeight: 600 }}>
                  {fmtRent(inv.amount_oere)}
                </td>
                <td style={{ padding: '10px 16px', color: '#6b7280' }}>
                  {inv.due_date ? fmtDate(inv.due_date) : '—'}
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <Chip variant={invoiceStatusVariant(inv.status)}>{invoiceStatusLabel(inv.status)}</Chip>
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); openPdfInNewTab(`/api/v1/invoices/${inv.id}/pdf`).catch((err: unknown) => alert(err instanceof Error ? err.message : 'Kunne ikke hente PDF')) }}
                    style={{ color: '#4f46e5', display: 'inline-flex' }}
                    title="Åbn PDF"
                  >
                    <ExternalLink size={14} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Inspection types ─────────────────────────────────────────────────────────

interface InspectionReport {
  id: string
  type: string
  status: string | null
  inspection_date: string | null
  created_at: string
}

function inspectionStatusVariant(s: string | null): 'green' | 'blue' | 'amber' | 'gray' | 'purple' | 'red' {
  switch (s) {
    case 'completed': return 'green'
    case 'in_progress': return 'blue'
    case 'draft': return 'gray'
    default: return 'gray'
  }
}

function inspectionStatusLabel(s: string | null): string {
  const map: Record<string, string> = {
    draft: 'Kladde', in_progress: 'Igangværende', completed: 'Afsluttet',
  }
  return s ? (map[s] ?? s) : 'Kladde'
}

function inspectionTypeLabel(t: string): string {
  return t === 'indflytning' ? 'Indflytningssyn' : t === 'fraflytning' ? 'Fraflytningssyn' : t
}

// ─── Syn Tab (inspection reports) ─────────────────────────────────────────────

function SynTab({ leaseId }: { leaseId: string }) {
  const navigate = useNavigate()
  const [reports, setReports] = React.useState<InspectionReport[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    apiFetch<InspectionReport[]>(`/api/v1/inspection-reports?lease_id=${leaseId}`)
      .then((res) => {
        // endpoint returns array directly
        setReports(Array.isArray(res) ? res : [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [leaseId])

  return (
    <div>
      {loading && (
        <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>
      )}

      {!loading && reports.length === 0 && (
        <div style={{
          background: '#fff', border: '1px solid #e8eaed', borderRadius: 10,
          padding: 40, textAlign: 'center',
        }}>
          <ClipboardList size={28} style={{ color: '#d1d5db', marginBottom: 8 }} />
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Ingen rapporter endnu</p>
        </div>
      )}

      {!loading && reports.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reports.map((report) => (
            <div
              key={report.id}
              style={{
                background: '#fff', border: '1px solid #e8eaed', borderRadius: 10,
                padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 8, background: '#eff6ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ClipboardList size={18} color="#4f46e5" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    {inspectionTypeLabel(report.type)}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {report.inspection_date ? fmtDate(report.inspection_date) : fmtDate(report.created_at)}
                  </div>
                </div>
                <Chip variant={inspectionStatusVariant(report.status)}>
                  {inspectionStatusLabel(report.status)}
                </Chip>
              </div>
              <button
                onClick={() => navigate(`/flyttesyn/${report.id}`)}
                style={{
                  background: '#4f46e5', color: '#fff', border: 'none',
                  borderRadius: 7, padding: '7px 14px', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                Åbn rapport
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Lejerlink Modal ──────────────────────────────────────────────────────────

function LejerLinkModal({ contractId, leaseId, onClose }: {
  contractId: string
  leaseId: string
  onClose: () => void
}) {
  const [generating, setGenerating] = React.useState(false)
  const [portalUrl, setPortalUrl] = React.useState<string | null>(null)
  const [expiresAt, setExpiresAt] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [copied, setCopied] = React.useState(false)

  const generateLink = React.useCallback(async () => {
    setGenerating(true)
    setError(null)
    try {
      // First fetch the primary renter on this contract
      const parties = await apiFetch<Array<{ renter_id: string; role: string; name: string }>>(`/api/v1/contracts/${contractId}/parties`)
      const primary = parties.find((p) => p.role === 'primary') ?? parties[0]
      if (!primary) {
        setError('Ingen lejer tilknyttet lejeforholdet. Tilføj en lejer under fanen Lejere først.')
        return
      }

      const res = await apiFetch<{ token: string; expires_at: string; portal_path: string; portal_url: string }>('/api/v1/lejer-portal/tokens', {
        method: 'POST',
        body: JSON.stringify({ renter_id: primary.renter_id, lease_id: leaseId }),
      })
      // The standalone tenant-portal owns this surface now — link there, not the
      // retired in-app /lejer/:token route.
      setPortalUrl(res.portal_url)
      setExpiresAt(res.expires_at)
    } catch (e: unknown) {
      setError((e instanceof Error ? e.message : null) ?? 'Kunne ikke generere link. Prøv igen.')
    } finally {
      setGenerating(false)
    }
  }, [contractId, leaseId])

  React.useEffect(() => {
    (async () => { await generateLink() })()
  }, [generateLink])

  const handleCopy = async () => {
    if (!portalUrl) return
    try {
      await navigator.clipboard.writeText(portalUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* ignore */
    }
  }

  const expiry = expiresAt ? new Date(expiresAt).toLocaleDateString('da-DK', {
    year: 'numeric', month: 'long', day: 'numeric',
  }) : null

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 32, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Link2 size={18} color="#4f46e5" />
            </div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Link genereret</h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1, padding: 4 }}
          >
            ×
          </button>
        </div>

        {generating && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>
            Genererer link…
          </div>
        )}

        {error && (
          <div>
            <p style={{ fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: 8, padding: 14, lineHeight: 1.5 }}>
              {error}
            </p>
            <Button variant="secondary" onClick={onClose} style={{ marginTop: 12 }}>Luk</Button>
          </div>
        )}

        {!generating && !error && portalUrl && (
          <>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
              Send dette link til lejeren — ingen adgangskode nødvendig. Linket giver adgang til lejerens egne oplysninger.
            </p>

            <div style={{
              background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8,
              padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 12, color: '#4f46e5', flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', lineHeight: 1.4 }}>
                {portalUrl}
              </span>
              <button
                onClick={handleCopy}
                style={{
                  background: copied ? '#dcfce7' : '#fff',
                  border: '1px solid ' + (copied ? '#86efac' : '#e5e7eb'),
                  borderRadius: 6, padding: '6px 10px', cursor: 'pointer',
                  color: copied ? '#16a34a' : '#6b7280',
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
                  transition: 'all 0.15s', flexShrink: 0,
                }}
                title="Kopiér link"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Kopieret' : 'Kopiér'}
              </button>
            </div>

            {expiry && (
              <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 20px' }}>
                Udløber {expiry}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={handleCopy} style={{ flex: 1 }}>
                {copied ? '✓ Kopieret!' : 'Kopiér link'}
              </Button>
              <Button variant="secondary" onClick={onClose}>Luk</Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Varsling Modal ───────────────────────────────────────────────────────────

function VarslingModal({
  contractId,
  preview,
  onClose,
  onSent,
}: {
  contractId: string
  preview: VarslingPreview
  onClose: () => void
  onSent: () => void
}) {
  const [sending, setSending] = React.useState(false)
  const [sendError, setSendError] = React.useState<string | null>(null)
  const [sent, setSent] = React.useState(false)

  function formatDKK(oere: number) {
    return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.'
  }

  async function sendVarsling() {
    setSending(true)
    setSendError(null)
    try {
      await apiFetch(`/api/v1/varsling/${contractId}/send`, { method: 'POST' })
      setSent(true)
      onSent()
    } catch (e: unknown) {
      setSendError(e instanceof Error ? e.message : 'Kunne ikke sende varsling.')
    } finally {
      setSending(false)
    }
  }

  const pct = preview.pct_increase_applied.toFixed(2).replace('.', ',')
  const cappedNote = preview.was_capped ? ' (begrænset af lejekontrakten)' : ''

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 24, width: 480,
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)', border: '1px solid #e8eaed',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Huslejeregulering — NPI</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Regulering gælder fra {new Date(preview.regulation_effective_date).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {/* NPI calculation */}
        <div style={{ background: '#f9fafb', borderRadius: 8, padding: 14, marginBottom: 14, fontSize: 13 }}>
          <div style={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', marginBottom: 8 }}>Beregning</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 16px' }}>
            <div style={{ color: '#6b7280' }}>Indeks {preview.from_year}</div>
            <div style={{ fontFamily: 'monospace', textAlign: 'right' }}>{preview.from_index.toFixed(1).replace('.', ',')}</div>
            <div style={{ color: '#6b7280' }}>Indeks {preview.to_year}</div>
            <div style={{ fontFamily: 'monospace', textAlign: 'right' }}>{preview.to_index.toFixed(1).replace('.', ',')}</div>
            <div style={{ color: '#6b7280', borderTop: '1px solid #e8eaed', paddingTop: 6, marginTop: 2 }}>Stigning</div>
            <div style={{ fontFamily: 'monospace', textAlign: 'right', borderTop: '1px solid #e8eaed', paddingTop: 6, marginTop: 2 }}>
              {pct} %{cappedNote}
            </div>
          </div>
        </div>

        {/* Rent comparison */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Nuværende husleje</div>
            <div style={{ fontWeight: 600, color: '#374151', fontFamily: 'monospace' }}>{formatDKK(preview.current_rent_oere)}</div>
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Ny husleje</div>
            <div style={{ fontWeight: 700, color: '#16a34a', fontFamily: 'monospace' }}>{formatDKK(preview.new_rent_oere)}</div>
          </div>
        </div>

        {/* Varsling deadline status */}
        <div style={{
          padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 12,
          background: preview.varsling_ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${preview.varsling_ok ? '#bbf7d0' : '#fecaca'}`,
          color: preview.varsling_ok ? '#15803d' : '#dc2626',
        }}>
          <strong>Varslingsfrist:</strong>{' '}
          Senest {new Date(preview.varsling_deadline).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}
          {!preview.varsling_ok && preview.warning && <div style={{ marginTop: 3 }}>{preview.warning}</div>}
        </div>

        {sendError && (
          <div style={{ background: '#fef2f2', color: '#dc2626', fontSize: 12, padding: '6px 10px', borderRadius: 6, marginBottom: 12, border: '1px solid #fecaca' }}>
            {sendError}
          </div>
        )}

        {sent && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: 13, fontWeight: 500, padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>
            ✓ Varsling sendt til lejer
          </div>
        )}

        <ComplianceDisclaimer />

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
            background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', cursor: 'pointer',
          }}>Luk</button>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); openPdfInNewTab(`/api/v1/varsling/${contractId}/pdf`).catch((err: unknown) => alert(err instanceof Error ? err.message : 'Kunne ikke hente PDF')) }}
            style={{
              padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              background: '#eff6ff', color: '#4f46e5', border: '1px solid #bfdbfe',
              textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
            }}
          >
            Hent PDF
          </a>
          <button
            disabled={!preview.varsling_ok || sending || sent}
            onClick={sendVarsling}
            style={{
              padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              background: preview.varsling_ok && !sending && !sent ? '#16a34a' : '#f3f4f6',
              color: preview.varsling_ok && !sending && !sent ? '#fff' : '#9ca3af',
              border: 'none', cursor: preview.varsling_ok && !sending && !sent ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            {sending ? 'Sender…' : 'Send til lejer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Opsigelse Modal (EOT4) ───────────────────────────────────────────────────
//
// Guided form: vælg part → grund → opsigelsesdato → bekræft.
// For udlejer + §171 nr.2 (nedrivning): kræver upload af dokumentationsbilag
// (POST /contracts/{id}/bilag) før opsigelsen kan indsendes — server-side
// HARD BLOCK afvises ellers med 422.

interface OpsigSuccess {
  contract_id: string
  opsagt_dato: string
  opsagt_af: string
  opsigelse_ground: string
  fraflytning_dato: string
  indsigelse_frist: string | null
  lease_status: string
}

function todayISO(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function OpsigeModal({
  contractId,
  onClose,
  onOpsagt,
}: {
  contractId: string
  onClose: () => void
  onOpsagt: (s: OpsigSuccess) => void
}) {
  const [step, setStep] = React.useState<1 | 2 | 3>(1)
  const [party, setParty] = React.useState<OpsigParty | null>(null)
  const [ground, setGround] = React.useState<string>('')
  const [opsagtDato, setOpsagtDato] = React.useState<string>(todayISO())
  const [bilagFile, setBilagFile] = React.useState<File | null>(null)
  const [bilagId, setBilagId] = React.useState<string | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<OpsigSuccess | null>(null)

  const grounds: OpsigGroundOption[] = party === 'lejer'
    ? LEJER_GROUNDS
    : party === 'udlejer' ? UDLEJER_GROUNDS : []
  const requiresBilag = party === 'udlejer' && ground === '§171_nr2'

  const goStep = (s: 1 | 2 | 3) => {
    setError(null)
    setStep(s)
  }

  const uploadBilag = async (file: File): Promise<string> => {
    setUploading(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const data = await apiFetch<{ id: string }>(`/api/v1/contracts/${contractId}/bilag`, {
        method: 'POST',
        body: form,
      }).catch((e: unknown) => {
        throw new Error(e instanceof Error && e.message ? e.message : 'Kunne ikke uploade bilag.')
      })
      return data.id
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async () => {
    if (!party || !ground || !opsagtDato) return
    setError(null)
    setSubmitting(true)
    try {
      let finalBilagId = bilagId
      if (requiresBilag && !finalBilagId) {
        if (!bilagFile) {
          throw new Error('Vælg et dokumentationsbilag (PDF) først.')
        }
        finalBilagId = await uploadBilag(bilagFile)
        setBilagId(finalBilagId)
      }
      const body: Record<string, unknown> = {
        opsagt_dato: opsagtDato,
        party,
        ground,
      }
      if (finalBilagId) body.opsigelse_dokumentation_bilag_id = finalBilagId
      const data = await apiFetch<OpsigSuccess>(
        `/api/v1/contracts/${contractId}/opsig`,
        { method: 'POST', body: JSON.stringify(body) },
      )
      setSuccess(data)
      onOpsagt(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Kunne ikke registrere opsigelsen.')
    } finally {
      setSubmitting(false)
    }
  }

  const fmtDanish = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString('da-DK', {
      day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  const stepDot = (n: 1 | 2 | 3, label: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 22, height: 22, borderRadius: 11,
          background: step >= n ? '#4f46e5' : '#e5e7eb',
          color: step >= n ? '#fff' : '#9ca3af',
          fontSize: 12, fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {n}
      </span>
      <span style={{
        fontSize: 12,
        color: step >= n ? '#111827' : '#9ca3af',
        fontWeight: step === n ? 600 : 500,
      }}>
        {label}
      </span>
    </div>
  )

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 560,
        boxShadow: '0 20px 60px rgba(0,0,0,0.20)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: 18,
        }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>
              Opsig lejeforhold
            </h2>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
              Lejeloven §§ 170, 171, 175 — varselberegningen sker server-side.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9ca3af', fontSize: 22, lineHeight: 1, padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {!success && (
          <div style={{ display: 'flex', gap: 18, marginBottom: 20 }}>
            {stepDot(1, 'Part')}
            {stepDot(2, 'Grund')}
            {stepDot(3, 'Dato')}
          </div>
        )}

        {success && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
            padding: 18, marginBottom: 16,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#15803d', marginBottom: 10 }}>
              ✓ Opsigelse registreret
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6, fontSize: 13 }}>
              <div style={{ color: '#6b7280' }}>Opsagt af</div>
              <div style={{ color: '#111827', fontWeight: 500 }}>
                {success.opsagt_af === 'udlejer' ? 'Udlejer' : 'Lejer'}
              </div>
              <div style={{ color: '#6b7280' }}>Grundlag</div>
              <div style={{ color: '#111827', fontWeight: 500 }}>
                {GROUND_LABEL[success.opsigelse_ground] ?? success.opsigelse_ground}
              </div>
              <div style={{ color: '#6b7280' }}>Opsagt dato</div>
              <div style={{ color: '#111827', fontWeight: 500 }}>{fmtDanish(success.opsagt_dato)}</div>
              <div style={{ color: '#6b7280' }}>Fraflytningsdato</div>
              <div style={{ color: '#111827', fontWeight: 700 }}>{fmtDanish(success.fraflytning_dato)}</div>
              {success.indsigelse_frist && (
                <>
                  <div style={{ color: '#6b7280' }}>Indsigelsesfrist</div>
                  <div style={{ color: '#111827', fontWeight: 500 }}>{fmtDanish(success.indsigelse_frist)}</div>
                </>
              )}
            </div>
            {success.opsagt_af === 'udlejer' && (
              <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); openPdfInNewTab(`/api/v1/contracts/${contractId}/opsig/pdf`).catch((err: unknown) => alert(err instanceof Error ? err.message : 'Kunne ikke hente PDF')) }}
                  style={{
                    padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                    background: '#eff6ff', color: '#4f46e5', border: '1px solid #bfdbfe',
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
                  }}
                >
                  Hent opsigelses-PDF
                </a>
              </div>
            )}
            {success.opsagt_af === 'udlejer' && <ComplianceDisclaimer />}
          </div>
        )}

        {!success && step === 1 && (
          <div>
            <p style={{ fontSize: 13, color: '#374151', marginTop: 0, marginBottom: 14 }}>
              Hvem opsiger lejeforholdet?
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {(['lejer', 'udlejer'] as OpsigParty[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => { setParty(p); setGround(''); goStep(2) }}
                  style={{
                    padding: '18px 14px', textAlign: 'left',
                    border: '1px solid ' + (party === p ? '#4f46e5' : '#e5e7eb'),
                    background: party === p ? '#eff6ff' : '#fff',
                    borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                    {p === 'lejer' ? 'Lejer opsiger' : 'Udlejer opsiger'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                    {p === 'lejer'
                      ? '3 mdr. til den 1. (beboelse) eller kortere efter kontrakten.'
                      : 'Kræver § 170/§ 171 grund. § 171 nr. 5 understøttes ikke.'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!success && step === 2 && party && (
          <div>
            <p style={{ fontSize: 13, color: '#374151', marginTop: 0, marginBottom: 14 }}>
              Vælg opsigelsesgrundlag
            </p>
            <select
              value={ground}
              onChange={(e) => setGround(e.target.value)}
              style={{
                height: 40, padding: '0 12px', borderRadius: 7,
                border: '1px solid #e8eaed', fontSize: 14, width: '100%',
                fontFamily: 'inherit', marginBottom: 10,
              }}
            >
              <option value="">— Vælg grund —</option>
              {grounds.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
            {ground && (() => {
              const opt = grounds.find((g) => g.value === ground)
              return opt?.helper ? (
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 14px', lineHeight: 1.5 }}>
                  {opt.helper}
                </p>
              ) : null
            })()}

            {requiresBilag && (
              <div style={{
                marginTop: 8, padding: '12px 14px', background: '#fff7ed',
                border: '1px solid #fed7aa', borderRadius: 7,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#9a3412', marginBottom: 8 }}>
                  Dokumentationsbilag påkrævet (PDF)
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null
                    setBilagFile(f)
                    setBilagId(null)
                  }}
                  style={{ fontSize: 12 }}
                />
                {bilagFile && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                    Valgt: {bilagFile.name}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
              <button
                type="button"
                onClick={() => goStep(1)}
                style={{
                  padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                  background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                ← Tilbage
              </button>
              <button
                type="button"
                disabled={!ground || (requiresBilag && !bilagFile)}
                onClick={() => goStep(3)}
                style={{
                  padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  background: ground && (!requiresBilag || bilagFile) ? '#4f46e5' : '#f3f4f6',
                  color: ground && (!requiresBilag || bilagFile) ? '#fff' : '#9ca3af',
                  border: 'none',
                  cursor: ground && (!requiresBilag || bilagFile) ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                Næste →
              </button>
            </div>
          </div>
        )}

        {!success && step === 3 && party && (
          <div>
            <p style={{ fontSize: 13, color: '#374151', marginTop: 0, marginBottom: 12 }}>
              Hvilken dato opsiges lejeforholdet pr.?
            </p>
            <input
              type="date"
              value={opsagtDato}
              onChange={(e) => setOpsagtDato(e.target.value)}
              style={{
                height: 40, padding: '0 12px', borderRadius: 7,
                border: '1px solid #e8eaed', fontSize: 14, width: '100%',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />

            <div style={{
              marginTop: 16, padding: 14, background: '#f9fafb',
              borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13,
            }}>
              <div style={{ color: '#6b7280', marginBottom: 4 }}>Opsigende part</div>
              <div style={{ color: '#111827', fontWeight: 600, marginBottom: 10 }}>
                {party === 'lejer' ? 'Lejer' : 'Udlejer'}
              </div>
              <div style={{ color: '#6b7280', marginBottom: 4 }}>Grundlag</div>
              <div style={{ color: '#111827', fontWeight: 600 }}>
                {GROUND_LABEL[ground] ?? ground}
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                Fraflytningsdato og — for udlejer — 6-ugers indsigelsesfrist beregnes server-side.
              </div>
            </div>

            {error && (
              <div style={{
                marginTop: 12, padding: '8px 12px', background: '#fef2f2',
                color: '#dc2626', fontSize: 12, borderRadius: 6, border: '1px solid #fecaca',
                lineHeight: 1.4,
              }}>
                {error}
              </div>
            )}

            <ComplianceDisclaimer />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
              <button
                type="button"
                onClick={() => goStep(2)}
                style={{
                  padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                  background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                ← Tilbage
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!opsagtDato || submitting || uploading}
                style={{
                  padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                  background: submitting || uploading ? '#f3f4f6' : '#dc2626',
                  color: submitting || uploading ? '#9ca3af' : '#fff',
                  border: 'none',
                  cursor: submitting || uploading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {uploading ? 'Uploader bilag…' : submitting ? 'Opsiger…' : 'Opsig lejeforhold'}
              </button>
            </div>
          </div>
        )}

        {success && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                background: '#4f46e5', color: '#fff', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Luk
            </button>
          </div>
        )}
      </div>
    </div>
  )
}


// ─── Underskrifter Card ───────────────────────────────────────────────────────

function UnderskrifterCard({
  contract,
  parties,
  onSign,
}: {
  contract: Contract
  parties: ContractParty[]
  onSign: (payload: { party?: 'landlord'; party_id?: string }) => Promise<void>
}) {
  const [signing, setSigning] = React.useState<string | null>(null) // 'landlord' | renter_id

  const handleSign = async (payload: { party?: 'landlord'; party_id?: string }) => {
    const key = payload.party ?? payload.party_id ?? ''
    setSigning(key)
    await onSign(payload)
    setSigning(null)
  }

  const tenantParties = parties.filter(
    (p) => p.role === 'primary_tenant' || p.role === 'co_tenant',
  )
  const guarantorParties = parties.filter((p) => p.role === 'guarantor')

  const signedCount =
    (contract.landlord_signed_at ? 1 : 0) +
    tenantParties.filter((p) => p.signed_at).length
  const requiredCount = 1 + tenantParties.length // landlord + all tenants
  const allSigned = contract.status === 'active'

  const roleLabel = (role: string) => {
    if (role === 'primary_tenant') return 'Primær lejer'
    if (role === 'co_tenant') return 'Medlejer'
    if (role === 'guarantor') return 'Kautionist'
    return role
  }

  const signedBadge = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600, color: '#16a34a',
    }}>
      <Check size={13} />
      Underskrevet
    </span>
  )

  const signBtn = (payload: { party?: 'landlord'; party_id?: string }, label = 'Markér som underskrevet') => {
    const key = payload.party ?? payload.party_id ?? ''
    const isLoading = signing === key
    return (
      <button
        disabled={!!signing}
        onClick={() => handleSign(payload)}
        style={{
          padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
          background: isLoading ? '#f3f4f6' : '#eff6ff',
          color: isLoading ? '#9ca3af' : '#4f46e5',
          border: '1px solid ' + (isLoading ? '#e5e7eb' : '#bfdbfe'),
          cursor: signing ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {isLoading ? 'Gemmer…' : label}
      </button>
    )
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden', marginTop: 20 }}>
      {/* Card header */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #e8eaed',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={SECTION_LABEL as React.CSSProperties}>UNDERSKRIFTER</span>
        <span style={{
          fontSize: 12, fontWeight: 500,
          color: allSigned ? '#16a34a' : '#6b7280',
        }}>
          {allSigned
            ? 'Aktiv – alle har underskrevet'
            : `Afventer underskrift · ${signedCount} / ${requiredCount}`}
        </span>
      </div>

      {/* Rows */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {/* Landlord row */}
          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '12px 20px', fontSize: 13, color: '#6b7280', width: 200, whiteSpace: 'nowrap' }}>
              Udlejer
            </td>
            <td style={{ padding: '12px 20px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
              {contract.landlord_signed_at
                ? <>{signedBadge} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>{fmtDate(contract.landlord_signed_at)}</span></>
                : signBtn({ party: 'landlord' })}
            </td>
          </tr>

          {/* Tenant rows */}
          {tenantParties.map((p, i) => (
            <tr key={p.renter_id} style={{ borderBottom: i < tenantParties.length - 1 || guarantorParties.length > 0 ? '1px solid #f3f4f6' : 'none' }}>
              <td style={{ padding: '12px 20px', fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
                <div>{p.name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{roleLabel(p.role)}</div>
              </td>
              <td style={{ padding: '12px 20px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
                {p.signed_at
                  ? <>{signedBadge} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>{fmtDate(p.signed_at)}</span></>
                  : signBtn({ party_id: p.renter_id })}
              </td>
            </tr>
          ))}

          {/* Guarantor rows (optional — informational only, not signable via this endpoint) */}
          {guarantorParties.map((p, i) => (
            <tr key={p.renter_id} style={{ borderBottom: i < guarantorParties.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <td style={{ padding: '12px 20px', fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
                <div>{p.name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                  {roleLabel(p.role)} <span style={{ color: '#d1d5db' }}>· valgfri</span>
                </div>
              </td>
              <td style={{ padding: '12px 20px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
                {p.signed_at
                  ? <>{signedBadge} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>{fmtDate(p.signed_at)}</span></>
                  : <span style={{ fontSize: 12, color: '#9ca3af' }}>valgfri</span>}
              </td>
            </tr>
          ))}

          {/* Empty state */}
          {parties.length === 0 && (
            <tr>
              <td colSpan={2} style={{ padding: '20px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                Ingen parter tilknyttet endnu.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ─── Page Shell ───────────────────────────────────────────────────────────────

export function LejeforholdDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [contract, setContract] = React.useState<Contract | null>(null)
  const [lease, setLease] = React.useState<Lease | null>(null)
  const [property, setProperty] = React.useState<Property | null>(null)
  const [parties, setParties] = React.useState<ContractParty[]>([])
  const [loading, setLoading] = React.useState(true)
  const [notFound, setNotFound] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<Tab>('overblik')
  const [lejerLinkOpen, setLejerLinkOpen] = React.useState(false)
  const [opsigOpen, setOpsigOpen] = React.useState(false)
  const [varslingPreview, setVarslingPreview] = React.useState<VarslingPreview | null>(null)
  const [varslingLoading, setVarslingLoading] = React.useState(false)
  const [varslingError, setVarslingError] = React.useState<string | null>(null)

  const loadParties = React.useCallback(async (contractId: string) => {
    try {
      const p = await apiFetch<ContractParty[]>(`/api/v1/contracts/${contractId}/parties`)
      setParties(Array.isArray(p) ? p : [])
    } catch {
      // non-fatal: checklist shows empty state
    }
  }, [])

  React.useEffect(() => {
    if (!id) return
    apiFetch<Contract>(`/api/v1/contracts/${id}`)
      .then(async (c) => {
        setContract(c)
        void loadParties(c.id)
        const l = await apiFetch<Lease>(`/api/v1/leases/${c.lease_id}`)
        setLease(l)
        const pData = await apiFetch<{ items: Property[] }>('/api/v1/properties')
        const p = pData.items.find((x) => x.id === l.property_id)
        if (p) setProperty(p)
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id, loadParties])

  async function handleSign(payload: { party?: 'landlord'; party_id?: string }) {
    if (!id) return
    try {
      const updated = await apiFetch<Contract>(`/api/v1/contracts/${id}/sign`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      setContract(updated)
      await loadParties(id)
    } catch {
      alert('Kunne ikke registrere underskrift.')
    }
  }

  async function genererVarsling() {
    if (!contract?.id) return
    setVarslingLoading(true)
    setVarslingError(null)
    try {
      const preview = await apiFetch<VarslingPreview>(`/api/v1/varsling/preview/${contract.id}`)
      setVarslingPreview(preview)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Kunne ikke hente varsling.'
      setVarslingError(msg)
    } finally {
      setVarslingLoading(false)
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>
      </AppLayout>
    )
  }

  if (notFound || !contract) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>Lejeforholdet blev ikke fundet.</p>
          <Link to="/lejeforhold"><Button variant="secondary">← Tilbage</Button></Link>
        </div>
      </AppLayout>
    )
  }

  const leaseSerial = lease?.serial_number != null ? String(lease.serial_number).padStart(3, '0') : '—'
  const contractSerial = contract.serial_number != null ? String(contract.serial_number).padStart(3, '0') : '—'
  const löbenr = property ? `${property.serial_label}-${leaseSerial}-${contractSerial}` : contractSerial
  const address = lease ? `${property?.address ?? '—'} · ${lease.unit_no}` : '—'

  return (
    <AppLayout>
      {/* Sticky header + tab bar — negative margins bleed over the <main> padding (28px)
          so the page content scrolls underneath without peeking through the gap */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: '#f7f8fa',
        margin: '-28px -28px 20px', padding: '28px 28px 0',
      }}>
        {/* Header */}
        <div style={{ padding: '0 0 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontFamily: 'monospace', fontSize: 14, fontWeight: 700,
              color: '#4f46e5', background: '#eff6ff', padding: '3px 8px', borderRadius: 5,
            }}>
              {löbenr}
            </span>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>{address}</h1>
              <span style={{ fontSize: 12, color: '#6b7280' }}>Lejeforhold · {contract.type.toUpperCase()}</span>
            </div>
            <Chip variant={statusVariant(contract.status)}>{statusLabel(contract.status)}</Chip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLejerLinkOpen(true)}
            >
              <Link2 size={13} style={{ marginRight: 5 }} />
              Lejerlink
            </Button>
            <Link to="/lejeforhold">
              <Button variant="secondary" size="sm">← Alle lejeforhold</Button>
            </Link>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e8eaed', overflowX: 'auto' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '11px 16px', fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#111827' : '#6b7280',
              background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'color 0.12s, border-color 0.12s',
            }}
          >
            {tab.label}
          </button>
        ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'overblik' && (
        <>
          <OverblikTab contract={contract} onUpdated={setContract} />
          <UnderskrifterCard contract={contract} parties={parties} onSign={handleSign} />

          {/* Opsigelses-status (vises hvis kontrakten er opsagt) */}
          {contract.opsagt_dato && (
            <div style={{
              marginTop: 16, padding: 16, background: '#fff7ed',
              border: '1px solid #fed7aa', borderRadius: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Lejeforholdet er opsagt
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6, fontSize: 13 }}>
                <div style={{ color: '#6b7280' }}>Opsagt af</div>
                <div style={{ color: '#111827', fontWeight: 500 }}>
                  {contract.opsagt_af === 'udlejer' ? 'Udlejer' : 'Lejer'}
                </div>
                {contract.opsigelse_ground && (
                  <>
                    <div style={{ color: '#6b7280' }}>Grundlag</div>
                    <div style={{ color: '#111827', fontWeight: 500 }}>
                      {GROUND_LABEL[contract.opsigelse_ground] ?? contract.opsigelse_ground}
                    </div>
                  </>
                )}
                <div style={{ color: '#6b7280' }}>Opsagt dato</div>
                <div style={{ color: '#111827', fontWeight: 500 }}>{fmtDate(contract.opsagt_dato)}</div>
                {contract.fraflytning_dato && (
                  <>
                    <div style={{ color: '#6b7280' }}>Fraflytningsdato</div>
                    <div style={{ color: '#111827', fontWeight: 700 }}>{fmtDate(contract.fraflytning_dato)}</div>
                  </>
                )}
                {contract.indsigelse_frist && (
                  <>
                    <div style={{ color: '#6b7280' }}>Indsigelsesfrist</div>
                    <div style={{ color: '#111827', fontWeight: 500 }}>{fmtDate(contract.indsigelse_frist)}</div>
                  </>
                )}
              </div>
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {contract.opsagt_af === 'udlejer' && (
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); openPdfInNewTab(`/api/v1/contracts/${contract.id}/opsig/pdf`).catch((err: unknown) => alert(err instanceof Error ? err.message : 'Kunne ikke hente PDF')) }}
                    style={{
                      padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                      background: '#fff', color: '#9a3412', border: '1px solid #fed7aa',
                      textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
                    }}
                  >
                    Hent opsigelses-PDF
                  </a>
                )}
                <Link
                  to={`/fraflytningsopgoerelser?contract=${contract.id}`}
                  style={{
                    padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                    background: '#fff', color: '#9a3412', border: '1px solid #fed7aa',
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
                  }}
                >
                  Opret fraflytningsopgørelse
                </Link>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {contract.nettoprisindeks && (
              <button
                onClick={genererVarsling}
                disabled={varslingLoading}
                style={{
                  padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                  background: varslingLoading ? '#f3f4f6' : '#eff6ff',
                  color: varslingLoading ? '#9ca3af' : '#4f46e5',
                  border: '1px solid #bfdbfe',
                  cursor: varslingLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {varslingLoading ? 'Beregner…' : 'Generer varsling'}
              </button>
            )}

            {!contract.opsagt_dato && contract.status === 'active' && (
              <button
                onClick={() => setOpsigOpen(true)}
                style={{
                  padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                  background: '#fff5f5', color: '#c92a2a', border: '1px solid #fecaca',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Opsig lejeforhold
              </button>
            )}
          </div>

          {varslingError && (
            <div style={{ marginTop: 8, padding: '6px 10px', background: '#fef2f2', color: '#dc2626', fontSize: 12, borderRadius: 6, border: '1px solid #fecaca' }}>
              {varslingError}
            </div>
          )}
        </>
      )}
      {activeTab === 'lejere' && <KommerSnart description="Lejeroversigt og partsstyring (MitID-underskrift) kommer i en kommende opdatering." />}
      {activeTab === 'opkraevning' && <OpkraevningTab contractId={contract.id} contractStatus={contract.status} />}
      {activeTab === 'fakturering' && <FaktureringTab contractId={contract.id} />}
      {activeTab === 'syn' && lease && <SynTab leaseId={lease.id} />}
      {activeTab === 'klausuler' && <KommerSnart description="§11-klausuler vil blive tilgængelige i en kommende opdatering." />}
      {activeTab === 'nogleetal' && <KommerSnart description="Nøgletal for lejeforholdet — huslejeindeks, lejeprisanalyse — kommer i en kommende opdatering." />}
      {activeTab === 'dokumenter' && <KommerSnart />}
      {activeTab === 'billeder' && <KommerSnart />}
      {activeTab === 'beskeder' && <KommerSnart />}
      {activeTab === 'opgaver' && <KommerSnart />}
      {activeTab === 'historik' && <KommerSnart description="Fuld hændelseslog for lejeforholdet kommer i en kommende opdatering." />}

      {lejerLinkOpen && lease && (
        <LejerLinkModal
          contractId={contract.id}
          leaseId={lease.id}
          onClose={() => setLejerLinkOpen(false)}
        />
      )}

      {varslingPreview && (
        <VarslingModal
          contractId={contract.id}
          preview={varslingPreview}
          onClose={() => setVarslingPreview(null)}
          onSent={() => setVarslingPreview(null)}
        />
      )}

      {opsigOpen && (
        <OpsigeModal
          contractId={contract.id}
          onClose={() => setOpsigOpen(false)}
          onOpsagt={(s) => {
            // Reflect new opsigelse state in the contract card without a full reload.
            setContract((prev) => prev ? {
              ...prev,
              opsagt_dato: s.opsagt_dato,
              opsagt_af: s.opsagt_af,
              opsigelse_ground: s.opsigelse_ground,
              fraflytning_dato: s.fraflytning_dato,
              indsigelse_frist: s.indsigelse_frist,
            } : prev)
          }}
        />
      )}
    </AppLayout>
  )
}
